import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import { on } from '@ember/modifier'
import { or } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import FidgetSpinnerWave from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type { ButtonAppearance } from '../ui-button/themes'
import didInsert from '@ember/render-modifiers/modifiers/did-insert'
import didUpdate from '@ember/render-modifiers/modifiers/did-update'
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy'

export const EXTERNAL_STATE_DEFAULT = 'default'
export const EXTERNAL_STATE_LOAD = 'load'
export const EXTERNAL_STATE_ERROR = 'error'
export const EXTERNAL_STATE_DONE = 'done'

export const INTERNAL_STATE_START = 'start'
export const INTERNAL_STATE_LOAD = 'load'
export const INTERNAL_STATE_COMPLETE = 'complete'
export const INTERNAL_STATE_READY = 'ready'

export type ExternalSubmitState =
  | typeof EXTERNAL_STATE_DEFAULT
  | typeof EXTERNAL_STATE_LOAD
  | typeof EXTERNAL_STATE_ERROR
  | typeof EXTERNAL_STATE_DONE

interface Args {
  type?: string
  text?: string
  defaultText: string
  state: ExternalSubmitState
  clickAction?: (e: Event) => void

  disabled?: boolean
  appearance?: ButtonAppearance
}

interface Signature {
  Element: HTMLSpanElement
  Args: Args
}

/**
 * This class creates a button which has 3 behaviours. Loading, Success, Error
 * The original idea of this button was to create a generic component which takes as state like
 * `load`, `error`, `done` or `default` to trigger a reaction on the button itself which can be used
 * to indicate if we are currently loading async data. You can see it as a fancy loading indicator.
 * One requirement was that each state can transition to a different state without becoming jerky experience,
 * where content simply get replaced without motion. The component reads the state input to animate from one
 * `state (load|done...)` to another.
 *
 * @Class SubmitButtonComponent
 *
 * @property {String} state - 'default' || 'load' || 'error' || 'done'
 * @property {Boolean} disabled - used to initially disable a component
 * @property {String} defaultText - The default text which is displayed which defaults to `submit`
 */
export class FormWrapperSubmitButton extends Component<Signature> {
  @service intl!: IntlService

  lastTimeout?: ReturnType<typeof setTimeout>

  get type() {
    return this.args.type || 'submit'
  }

  get defaultText() {
    return this.args.text || this.args.defaultText || this.intl.t('submit')
  }

  @action
  clickAction(e: Event) {
    this.args.clickAction?.(e)
  }

  /**
   * Can be one of 'default' 'load' 'complete' 'ready'
   * Each dictate what animations are played, how the content elements are moved.
   *
   * start - the first state, no animations
   * load - slides the text out, load spinner in
   * complete - slides load spinner out, status element in (check or a cross)
   * ready - same as start, but is animated into (slides status element out, text in)
   */
  @tracked
  internalState: string = INTERNAL_STATE_START

  get internalStateClass() {
    return {
      [INTERNAL_STATE_START]: 'state-start',
      [INTERNAL_STATE_LOAD]: 'state-load',
      [INTERNAL_STATE_COMPLETE]: 'state-complete',
      [INTERNAL_STATE_READY]: 'state-ready',
    }[this.internalState]
  }

  // Reads the external state to determine if the status element should display success or fail
  get isError() {
    return this.args.state === EXTERNAL_STATE_ERROR
  }

  get isAnimationPlaying() {
    return this.internalState === INTERNAL_STATE_LOAD || this.internalState === INTERNAL_STATE_COMPLETE
  }

  @action
  didUpdateState(_element: HTMLElement, [externalState]: string[]) {
    // clear any timeouts
    clearTimeout(this.lastTimeout)

    const previousInternalState = this.internalState

    let newState = INTERNAL_STATE_START

    const isLoad = externalState === EXTERNAL_STATE_LOAD
    const isDoneOrError = externalState === EXTERNAL_STATE_DONE || externalState === EXTERNAL_STATE_ERROR

    if (isLoad) {
      newState = INTERNAL_STATE_LOAD
    } else if (previousInternalState === INTERNAL_STATE_LOAD && isDoneOrError) {
      newState = INTERNAL_STATE_COMPLETE
      // must have come from load state. want to make sure the state goes to a ready state after 3 seconds.
      const setReadyState = () => {
        if (this && !this.isDestroying && !this.isDestroyed) {
          this.internalState = INTERNAL_STATE_READY
        }
      }
      const timeout = setTimeout(setReadyState, 3000)
      this.lastTimeout = timeout
    }

    this.internalState = newState
  }

  @action
  onDestroy() {
    // clear any timeouts
    clearTimeout(this?.lastTimeout)
  }

  <template>
    <span
      class="form-wrapper-submit-button inline-block"
      {{didInsert this.didUpdateState @state}}
      {{didUpdate this.didUpdateState @state}}
      {{willDestroy this.onDestroy}}
      ...attributes
    >
      <UiButton
        data-test-submit-state={{@state}}
        class="overflow-hidden"
        disabled={{or @disabled this.isAnimationPlaying}}
        type={{this.type}}
        @appearance={{@appearance}}
        {{on "click" this.clickAction}}
      >
        <span class="relative">
          <content-text class="inline {{this.internalStateClass}}">
            {{this.defaultText}}
          </content-text>
          <content-spinner class="inline {{this.internalStateClass}}">
            <FidgetSpinnerWave @small={{true}} @centered={{true}} />
          </content-spinner>
          <content-status class="inline {{this.internalStateClass}}">
            <PhosphorIcon
              data-test-complete-icon
              @icon={{if this.isError "phosphor-x-bold" "phosphor-check-bold"}}
              class={{if this.isError "text-watermelony-red-300" "text-ms-green-300"}}
            />
          </content-status>

          {{! ensures button is always correct width, in spite of the absolute/moving elements above. }}
          <span class="invisible">
            {{this.defaultText}}
          </span>
        </span>
      </UiButton>
    </span>
  </template>
}

export default FormWrapperSubmitButton
